import { z } from 'zod'
import {
  PROJECT_ROLES,
  CRITICALITY_LEVELS,
  FULFILLMENT_VALUES,
  MESSAGE_ROLES,
  UPLOAD_FORMATS,
  TASK_TYPES,
  TASK_STATUSES,
} from './constants.js'

// --- Auth ---

export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
})

export const SignupSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  name: z.string().min(1).max(200),
})

// --- Projects ---

export const CreateProjectSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
})

export const UpdateProjectSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(5000).optional(),
})

export const InviteMemberSchema = z.object({
  email: z.string().email(),
  role: z.enum(PROJECT_ROLES),
})

// --- Uploads ---

export const UploadMappingSchema = z.object({
  sessionIdField: z.string().optional(),
  roleField: z.string(),
  contentField: z.string(),
  timestampField: z.string().optional(),
  sessionTimestampField: z.string().optional(),
  metadataFields: z.array(z.string()).optional(),
})

export const ConfirmUploadSchema = z.object({
  mapping: UploadMappingSchema,
})

// --- Tags ---

export const CreateTagSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(5000).optional(),
  parentId: z.string().nullable().optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  shortcut: z.string().max(1).nullable().optional(),
  sortOrder: z.number().int().optional(),
})

export const UpdateTagSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(5000).nullable().optional(),
  parentId: z.string().nullable().optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  shortcut: z.string().max(1).nullable().optional(),
  sortOrder: z.number().int().optional(),
})

export const AssignTagSchema = z.object({
  tagId: z.string(),
})

// --- Rubrics ---

export const RubricCriterionSchema = z.object({
  description: z.string().min(1).max(2000),
  criticality: z.enum(CRITICALITY_LEVELS),
  sortOrder: z.number().int().default(0),
})

export const CreateRubricSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
  tagId: z.string().optional(),
  criteria: z.array(RubricCriterionSchema).min(1),
})

export const UpdateRubricSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
  tagId: z.string().nullable().optional(),
  criteria: z.array(RubricCriterionSchema).min(1),
  changeSummary: z.string().max(1000).optional(),
})

// --- Evaluations ---

export const GradeSchema = z.object({
  criteriaId: z.string(),
  fulfilled: z.enum(FULFILLMENT_VALUES),
  notes: z.string().max(5000).optional(),
})

export const SaveEvaluationSchema = z.object({
  rubricId: z.string(),
  rubricVersionId: z.string(),
  grades: z.array(GradeSchema),
})

// --- Highlights ---

export const CreateHighlightSchema = z.object({
  messageId: z.string(),
  highlightTypeId: z.string(),
  startOffset: z.number().int().min(0),
  endOffset: z.number().int().min(0),
})

// --- Comments ---

export const CreateCommentSchema = z.object({
  highlightId: z.string().optional(),
  parentId: z.string().optional(),
  content: z.string().min(1).max(10000),
})

// --- Tasks ---

export const TaskFilterSchema = z.object({
  search: z.string().max(500).optional(),
  tagIds: z.array(z.string()).optional(),
  tagMode: z.enum(['any', 'all']).optional(),
  excludeTagIds: z.array(z.string()).optional(),
  untagged: z.boolean().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  minMessages: z.number().int().optional(),
  maxMessages: z.number().int().optional(),
  evaluated: z.enum(['yes', 'no']).optional(),
  evalRubricId: z.string().optional(),
  bookmarked: z.boolean().optional(),
  myActivity: z.boolean().optional(),
})

export const CreateTaskSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
  type: z.enum(TASK_TYPES),
  sessionIds: z.array(z.string()).min(1).optional(),
  filter: TaskFilterSchema.optional(),
  rubricId: z.string().optional(),
  assigneeId: z.string().optional(),
  maskPii: z.boolean().default(false),
  expiresAt: z.string().datetime().optional(),
}).refine(
  data => (data.sessionIds && data.sessionIds.length > 0) || data.filter,
  { message: 'Either sessionIds or filter must be provided' }
)

export const CreateTaskInviteSchema = z.object({
  email: z.union([z.string().email(), z.literal('')]).optional(),
  expiresAt: z.string().datetime().optional(),
})

// --- Highlight Types ---

export const CreateHighlightTypeSchema = z.object({
  name: z.string().min(1).max(100),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  masksContent: z.boolean().default(false),
})

// --- Bulk Highlights ---

export const BulkHighlightSearchSchema = z.object({
  query: z.string().min(1).max(1000),
  limit: z.number().int().min(1).max(500).optional(),
})

export const BulkHighlightApplySchema = z.object({
  query: z.string().min(1).max(1000),
  highlightTypeId: z.string().min(1),
})

// --- Embeddings ---

export const EmbeddingConfigSchema = z.object({
  provider: z.enum(['openai', 'custom']),
  model: z.string().min(1).max(200),
  dimensions: z.number().int().min(1).max(10000).optional(),
  baseUrl: z.union([z.string().url().max(2000), z.literal('')]).optional().nullable(),
  apiKey: z.string().max(500).optional(),
  userRoles: z.array(z.string().max(50)).optional(),
  assistantRoles: z.array(z.string().max(50)).optional(),
})

export const EmbedSessionsSchema = z.object({
  sessionIds: z.array(z.string()).min(1).max(10000),
})

// --- Clusters ---

export const RunClusteringSchema = z.object({
  k: z.number().int().min(2).max(100),
})

export const UpdateClusterSchema = z.object({
  label: z.string().max(500).optional(),
  description: z.string().max(5000).optional(),
})

// --- Similarity ---

export const SimilaritySearchSchema = z.object({
  text: z.string().min(1).max(10000),
  limit: z.number().int().min(1).max(100).optional(),
})

// --- Bookmarks ---

export const BookmarkSchema = z.object({
  note: z.string().max(1000).optional(),
})

// --- Saved Searches ---

export const SaveSearchSchema = z.object({
  name: z.string().min(1).max(200),
  query: z.string().max(1000).default(''),
  filters: z.object({}).passthrough().optional(),
})

// --- Notifications ---

export const MarkNotificationsReadSchema = z.object({
  ids: z.array(z.string()).min(1).max(100),
})

// --- Predictions ---

export const PredictionConfigSchema = z.object({
  provider: z.enum(['openai', 'google', 'anthropic', 'custom']).optional(),
  model: z.string().min(1).max(200).optional(),
  baseUrl: z.union([z.string().url().max(2000), z.literal('')]).optional().nullable(),
  apiKey: z.string().max(500).optional(),
  promptTemplate: z.string().max(50000).optional(),
  defaultGranularity: z.enum(['session', 'message', 'author']).optional(),
  delayMs: z.number().int().min(0).max(60000).optional(),
  maxRetries: z.number().int().min(0).max(10).optional(),
  concurrency: z.number().int().min(1).max(20).optional(),
})

export const RunPredictionSchema = z.object({
  granularity: z.enum(['session', 'message', 'author']).default('session'),
  tagIds: z.array(z.string()).optional(),
  filter: z.object({
    search: z.string().max(500).optional(),
    tagIds: z.array(z.string()).optional(),
    tagMode: z.enum(['any', 'all']).optional(),
    excludeTagIds: z.array(z.string()).optional(),
    untagged: z.boolean().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    minMessages: z.number().int().optional(),
    maxMessages: z.number().int().optional(),
  }).optional(),
  model: z.string().max(200).optional(),
  delayMs: z.number().int().min(0).max(60000).optional(),
  concurrency: z.number().int().min(1).max(20).optional(),
})

export const PredictSessionTagsSchema = z.object({
  tagIds: z.array(z.string()).optional(),
  granularity: z.enum(['session', 'message', 'author']).default('session'),
})

// --- Scenario Generator ---

import { DETAIL_TYPES, INCLUSION_FREQUENCIES } from './constants.js'

export const CreatePackSchema = z.object({
  label: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
})

export const UpdatePackSchema = z.object({
  label: z.string().min(1).max(200).optional(),
  description: z.string().max(5000).nullable().optional(),
})

export const CreateScenarioSchema = z.object({
  content: z.string().min(1).max(50000),
  sortOrder: z.number().int().optional(),
})

export const UpdateScenarioSchema = z.object({
  content: z.string().min(1).max(50000).optional(),
  sortOrder: z.number().int().optional(),
})

export const ReorderScenariosSchema = z.object({
  ids: z.array(z.string()).min(1),
})

export const CreateDetailSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
  type: z.enum(DETAIL_TYPES).default('enum'),
  options: z.array(z.string().min(1).max(500)).optional(),
  rangeMin: z.number().optional(),
  rangeMax: z.number().optional(),
})

export const UpdateDetailSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(5000).nullable().optional(),
  type: z.enum(DETAIL_TYPES).optional(),
  options: z.array(z.string().min(1).max(500)).optional(),
  rangeMin: z.number().nullable().optional(),
  rangeMax: z.number().nullable().optional(),
})

export const CreateDetailConditionSchema = z.object({
  dependsOnDetailId: z.string(),
  dependsOnValues: z.array(z.string()).min(1),
})

export const SetScenarioDetailsSchema = z.object({
  detailIds: z.array(z.string()),
})

const ScopeEntrySchema = z.object({
  detailId: z.string(),
  detailValue: z.string(),
})

export const CreateCriterionSchema = z.object({
  description: z.string().min(1).max(2000),
  criticality: z.enum(CRITICALITY_LEVELS),
  expectedAnswer: z.string().max(5000).optional(),
  scope: z.array(ScopeEntrySchema).max(25).optional(),
})

export const UpdateCriterionSchema = z.object({
  description: z.string().min(1).max(2000).optional(),
  criticality: z.enum(CRITICALITY_LEVELS).optional(),
  expectedAnswer: z.string().max(5000).optional(),
})

export const SetScopeSchema = z.object({
  scope: z.array(ScopeEntrySchema).max(25),
})

export const SplitCriterionSchema = z.object({
  newScope: z.array(ScopeEntrySchema).max(25).min(1),
})

export const ReorderCriteriaSchema = z.object({
  ids: z.array(z.string()),
})

// --- Noise ---

export const SetOmissionSchema = z.object({
  omissionFreq: z.enum(INCLUSION_FREQUENCIES),
})

export const SetIrrelevantDetailsSchema = z.object({
  detailIds: z.array(z.string()),
})

export const SetInclusionFreqSchema = z.object({
  inclusionFreq: z.enum(INCLUSION_FREQUENCIES),
})

export const CreateNoiseSnippetSchema = z.object({
  content: z.string().min(1).max(2000),
})

export const UpdateNoiseSnippetSchema = z.object({
  content: z.string().min(1).max(2000).optional(),
})

// --- Style Presets ---

export const CreateStylePresetSchema = z.object({
  label: z.string().min(1).max(200),
  form: z.string().max(100).nullable().optional(),
  tone: z.string().max(500).nullable().optional(),
  customPrompt: z.string().max(5000).nullable().optional(),
  examples: z.array(z.string().min(1).max(5000)).max(5).nullable().optional(),
})

export const UpdateStylePresetSchema = z.object({
  label: z.string().min(1).max(200).optional(),
  form: z.string().max(100).nullable().optional(),
  tone: z.string().max(500).nullable().optional(),
  customPrompt: z.string().max(5000).nullable().optional(),
  examples: z.array(z.string().min(1).max(5000)).max(5).nullable().optional(),
})

export const ImportExamplesSchema = z.object({
  sessionIds: z.array(z.string()).min(1).max(10),
})

// --- Generation ---

export const GenerationConfigSchema = z.object({
  provider: z.enum(['openai', 'anthropic', 'google', 'custom']),
  model: z.string().min(1).max(100),
  apiKey: z.string().max(500).optional(),
  baseUrl: z.string().max(500).optional(),
})

export const GenerateSchema = z.object({
  combinations: z.array(z.record(z.string(), z.string())).optional(),
  detailValueFilter: z.record(z.string(), z.array(z.string())).optional(),
  stylePresetIds: z.array(z.string()).optional(),
  noiseSnippetRange: z.object({
    min: z.number().int().min(0).max(10).default(0),
    max: z.number().int().min(0).max(10).default(2),
  }).optional(),
  count: z.number().int().min(1).max(100),
})

export const CreateHumanGenerationSchema = z.object({
  content: z.string().min(1).max(50000),
  configSnapshot: z.object({
    detailValues: z.record(z.string(), z.string()),
    omittedDetails: z.array(z.string()).optional(),
    irrelevantDetailsUsed: z.array(z.string()).optional(),
    noiseSnippetsUsed: z.array(z.string()).optional(),
    stylePresetId: z.string().nullable().optional(),
  }),
})

export const UpdateGenerationSchema = z.object({
  content: z.string().min(1).max(50000),
})

export const ForkGenerationSchema = z.object({
  content: z.string().min(1).max(50000),
})

export const MoreLikeThisSchema = z.object({
  count: z.number().int().min(1).max(20),
})

export const ExportGenerationsSchema = z.object({
  ids: z.array(z.string()).optional(),
  format: z.enum(['json', 'csv']),
})

// --- Scenario Pool ---

export const AddToPoolSchema = z.object({
  generationIds: z.array(z.string()).min(1).max(100),
})

export const UpdatePoolItemSchema = z.object({
  content: z.string().min(1).max(50000).optional(),
  notes: z.string().max(5000).nullable().optional(),
})

export const DeletePoolItemsSchema = z.object({
  ids: z.array(z.string()).min(1).max(500),
})
